import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Calendar, Clock, MapPin, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { resolveSrc, formatDate } from '../lib/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RandIcon = ({ size = 14 }) => (
  <span
    aria-hidden="true"
    className="inline-flex items-center justify-center font-heading font-medium leading-none"
    style={{ width: size, height: size, fontSize: size * 1.1 }}
  >
    R
  </span>
);

const Stat = ({ icon, label, value }) => (
  <div className="border-l-2 border-primary/40 pl-4">
    <div className="flex items-center gap-2 text-text-muted">
      {icon}
      <span className="font-mono text-xs uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-heading text-text-main text-lg mt-1">{value}</div>
  </div>
);

// ---------------- Lightbox modal ----------------
function Lightbox({ photos, index, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onNav(-1);
      else if (e.key === 'ArrowRight') onNav(1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNav]);

  const photo = photos[index];

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4 md:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center glass text-text-main hover:text-primary transition-colors"
      >
        <X size={22} />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNav(-1);
            }}
            aria-label="Previous"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center glass text-text-main hover:text-primary transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNav(1);
            }}
            aria-label="Next"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center glass text-text-main hover:text-primary transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      <motion.div
        key={photo.id || index}
        className="max-w-5xl w-full flex flex-col items-center"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={resolveSrc(photo.url)}
          alt={photo.caption || 'Progress photo'}
          className="max-h-[78vh] w-auto max-w-full object-contain border border-border"
        />
        <div className="mt-4 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
            {formatDate(photo.taken_at)}
          </div>
          {photo.caption && (
            <div className="font-heading text-lg md:text-xl text-text-main mt-1">{photo.caption}</div>
          )}
          <div className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2">
            {index + 1} / {photos.length}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------------- Single project block (Featured-style) ----------------
function CurrentProjectBlock({ project, index, onOpen }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const isReversed = index % 2 === 1;
  const photos = project.photos || [];
  const hero = photos[0];
  const thumbs = photos.slice(1, 5);
  const extra = photos.length - 5;

  return (
    <section
      ref={ref}
      data-testid={`current-project-${project.id}`}
      className={`py-20 md:py-28 ${index % 2 === 0 ? 'bg-background' : 'bg-surface border-y border-border'}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start ${
            isReversed ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {hero ? (
              <>
                <button
                  type="button"
                  onClick={() => onOpen(project, 0)}
                  data-testid={`current-hero-${project.id}`}
                  className="image-hover-container aspect-[4/3] overflow-hidden mb-4 block w-full group relative"
                >
                  <img src={resolveSrc(hero.url)} alt={project.title} className="w-full h-full object-cover" />
                  <span className="absolute bottom-3 left-3 glass px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary">
                    {formatDate(hero.taken_at)}
                  </span>
                </button>
                {thumbs.length > 0 && (
                  <div className="grid grid-cols-4 gap-3">
                    {thumbs.map((img, idx) => {
                      const showExtra = extra > 0 && idx === thumbs.length - 1;
                      return (
                        <button
                          type="button"
                          key={img.id || idx}
                          onClick={() => onOpen(project, idx + 1)}
                          className="aspect-square overflow-hidden border border-border hover:border-primary transition-colors relative group"
                        >
                          <img src={resolveSrc(img.url)} alt={img.caption || `${project.title} ${idx + 2}`} className="w-full h-full object-cover" />
                          {showExtra && (
                            <span className="absolute inset-0 bg-background/70 flex items-center justify-center font-heading text-2xl text-text-main">
                              +{extra}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="aspect-[4/3] flex items-center justify-center border border-border bg-surface text-text-muted font-mono text-xs uppercase tracking-widest">
                Progress photos coming soon
              </div>
            )}
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
              </span>
              <span className="overline">Current Project // {String(index + 1).padStart(2, '0')}</span>
            </div>
            <span className="inline-block font-mono text-xs uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 mb-4">
              {project.status || 'In Progress'}
            </span>
            <h2 className="font-heading font-light text-3xl md:text-4xl lg:text-5xl text-text-main tracking-tight">
              {project.title}
            </h2>
            <p className="mt-6 text-text-muted font-body text-base md:text-lg leading-relaxed">
              {project.scope}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6">
              <Stat icon={<Calendar size={14} />} label="Start Date" value={project.start_date} />
              <Stat icon={<Clock size={14} />} label="Duration" value={project.duration} />
              <Stat icon={<RandIcon size={14} />} label="Project Value" value={project.value_range} />
              {project.location && (
                <Stat icon={<MapPin size={14} />} label="Location" value={project.location} />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------------- Main ----------------
export default function CurrentProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { photos, index }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/current-projects`);
        if (active) setProjects(res.data || []);
      } catch (e) {
        console.error('Error fetching current projects:', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const openLightbox = (project, index) => {
    const photos = project.photos || [];
    if (photos.length === 0) return;
    setLightbox({ photos, index });
  };

  const navLightbox = (dir) =>
    setLightbox((lb) => {
      if (!lb) return lb;
      const n = lb.photos.length;
      return { ...lb, index: (lb.index + dir + n) % n };
    });

  if (loading) {
    return (
      <section className="py-28 flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={28} />
      </section>
    );
  }

  if (projects.length === 0) {
    return (
      <section className="py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="overline mb-4 block">On Site Now</span>
          <h2 className="font-heading font-light text-3xl md:text-4xl text-text-main">
            No live projects to show right now
          </h2>
          <p className="mt-5 text-text-muted font-body">
            We&apos;re between milestones on our public updates. Check back soon to follow our current
            builds&apos; progress.
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      {projects.map((project, idx) => (
        <CurrentProjectBlock key={project.id} project={project} index={idx} onOpen={openLightbox} />
      ))}

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            photos={lightbox.photos}
            index={lightbox.index}
            onClose={() => setLightbox(null)}
            onNav={navLightbox}
          />
        )}
      </AnimatePresence>
    </>
  );
}
